import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useRoute, Link } from 'wouter';
import { endpoints } from '@/lib/api';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { StatusBadge, StatusIcon } from '@/components/ui/StatusBadge';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { PelicanConsole } from '@/components/PelicanConsole';
import {
  ArrowLeft,
  Play,
  Square,
  RotateCw,
  Zap,
  Folder,
  FileText,
  ExternalLink,
  RefreshCw,
} from 'lucide-react';
import { formatBytes } from '@/lib/format';
import { cn } from '@/lib/utils';

const REFETCH = 5_000;

export function PelicanServerRoute() {
  const [, params] = useRoute<{ id: string }>('/integrations/pelican/:id');
  const id = params?.id ?? '';
  const qc = useQueryClient();

  const statsQ = useQuery({
    queryKey: ['pelican', 'stats'],
    queryFn: endpoints.integrations.pelican.stats,
    refetchInterval: 60_000,
  });
  const servers = statsQ.data?.pelican?.servers ?? [];
  const server = servers.find((s) => s.identifier === id || s.uuid === id);

  const detailQ = useQuery({
    queryKey: ['pelican', 'server', id],
    queryFn: () => endpoints.integrations.pelican.server(id),
    refetchInterval: REFETCH,
    enabled: !!id,
  });

  const filesQ = useQuery({
    queryKey: ['pelican', 'files', id, '/'],
    queryFn: () => endpoints.integrations.pelican.files(id, '/'),
    refetchInterval: 10_000,
    enabled: !!id,
    retry: false,
  });

  const power = useMutation({
    mutationFn: (signal: 'start' | 'stop' | 'restart' | 'kill') =>
      endpoints.integrations.pelican.power(id, signal),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pelican'] });
      setTimeout(() => qc.invalidateQueries({ queryKey: ['pelican', 'server', id] }), 2000);
    },
  });

  if (!id) return <div className="text-muted-foreground">invalid server id</div>;

  return (
    <div className="space-y-6">
      <Link href="/integrations">
        <a className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
          <ArrowLeft className="h-3.5 w-3.5" />
          all apps
        </a>
      </Link>

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <StatusIcon status={server?.status ?? 'unknown'} />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              {server?.name ?? id}
            </h1>
            <p className="text-xs text-muted-foreground font-mono">
              {id} · panel
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={server?.status ?? 'unknown'} />
          {server?.isSuspended && <StatusBadge status="paused" label="suspended" />}
          <ActionButton
            icon={Play}
            label="Start"
            tone="success"
            onClick={() => power.mutate('start')}
            disabled={server?.status === 'running' || power.isPending}
          />
          <ActionButton
            icon={Square}
            label="Stop"
            tone="danger"
            onClick={() => power.mutate('stop')}
            disabled={server?.status === 'offline' || power.isPending}
          />
          <ActionButton
            icon={RotateCw}
            label="Restart"
            onClick={() => power.mutate('restart')}
            disabled={server?.status !== 'running' || power.isPending}
          />
          <ActionButton
            icon={Zap}
            label="Kill"
            tone="danger"
            onClick={() => {
              if (confirm(`Send SIGKILL to ${server?.name ?? id}? This is hard.`)) {
                power.mutate('kill');
              }
            }}
            disabled={server?.status !== 'running' || power.isPending}
          />
        </div>
      </div>

      {power.isError && (
        <div className="rounded-md border border-rose-500/30 bg-rose-500/5 p-3 text-sm text-rose-300">
          {String((power.error as Error)?.message ?? 'unknown error')}
        </div>
      )}
      {power.isSuccess && (
        <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-sm text-emerald-300">
          {power.data.signal} sent to {power.data.id}.
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile
          label="Status"
          value={server?.status ?? '—'}
          {...(server?.node ? { sub: server.node } : {})}
        />
        <Tile
          label="RAM"
          value={`${formatBytes(server?.resources.memoryMb ?? 0)}`}
          sub={`of ${formatBytes(server?.limits.memoryMb ?? 0)}`}
        />
        <Tile
          label="CPU"
          value={`${server?.resources.cpuPercent ?? 0}%`}
          sub={`limit ${server?.limits.cpuPercent ?? 0}%`}
        />
        <Tile
          label="Disk"
          value={`${formatBytes(server?.resources.diskMb ?? 0)}`}
          sub={`of ${formatBytes(server?.limits.diskMb ?? 0)}`}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card>
          <CardHeader>
            <CardTitle>Limits</CardTitle>
          </CardHeader>
          <CardBody>
            {server ? (
              <div className="space-y-3 text-sm">
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-muted-foreground">Memory</span>
                    <span className="tabular-nums">
                      {formatBytes(server.resources.memoryMb)} / {formatBytes(server.limits.memoryMb)}
                    </span>
                  </div>
                  <ProgressBar value={(server.resources.memoryMb / Math.max(server.limits.memoryMb, 1)) * 100} />
                </div>
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-muted-foreground">CPU</span>
                    <span className="tabular-nums">
                      {server.resources.cpuPercent.toFixed(0)}% / {server.limits.cpuPercent}%
                    </span>
                  </div>
                  <ProgressBar value={(server.resources.cpuPercent / Math.max(server.limits.cpuPercent, 1)) * 100} />
                </div>
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-muted-foreground">Disk</span>
                    <span className="tabular-nums">
                      {formatBytes(server.resources.diskMb)} / {formatBytes(server.limits.diskMb)}
                    </span>
                  </div>
                  <ProgressBar value={(server.resources.diskMb / Math.max(server.limits.diskMb, 1)) * 100} />
                </div>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">loading…</div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Folder className="h-3.5 w-3.5" />
              Files
            </CardTitle>
            <button
              onClick={() => filesQ.refetch()}
              className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
            >
              <RefreshCw className={cn('h-3 w-3', filesQ.isFetching && 'animate-spin')} />
              refresh
            </button>
          </CardHeader>
          <CardBody>
            {filesQ.isLoading ? (
              <div className="text-sm text-muted-foreground">listing…</div>
            ) : filesQ.isError || (filesQ.data && filesQ.data.available === false) ? (
              <div className="text-sm text-muted-foreground space-y-2">
                <p>File browser not available via the application API key.</p>
                <p className="text-xs">
                  The <code className="rounded bg-muted px-1 py-0.5">papp_</code> key has
                  admin scope but file listing lives on the client API. Open the
                  panel's full file manager for this server:
                </p>
                <a
                  href={`${typeof window !== 'undefined' && window.location.protocol}//${typeof window !== 'undefined' && window.location.hostname}:80`}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex items-center gap-1 text-xs text-foreground hover:underline"
                >
                  open Pelican panel <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            ) : filesQ.data?.items && filesQ.data.items.length > 0 ? (
              <ul className="space-y-1 text-sm font-mono">
                {filesQ.data.items.slice(0, 20).map((item, i) => {
                  const attrs = (item as { attributes?: { name?: string; is_file?: boolean; size?: number } }).attributes ?? (item as { name?: string; is_file?: boolean; size?: number });
                  return (
                    <li key={i} className="flex items-center gap-2">
                      {attrs.is_file ? (
                        <FileText className="h-3 w-3 text-muted-foreground" />
                      ) : (
                        <Folder className="h-3 w-3 text-muted-foreground" />
                      )}
                      <span className="truncate flex-1">{attrs.name ?? '?'}</span>
                      {!attrs.is_file && attrs.size !== undefined && (
                        <span className="text-xs text-muted-foreground tabular-nums">
                          {formatBytes(attrs.size)}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="text-sm text-muted-foreground">empty</div>
            )}
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Console</CardTitle>
          <span className="text-xs text-muted-foreground">live terminal</span>
        </CardHeader>
        <CardBody>
          <PelicanConsole serverId={id} />
        </CardBody>
      </Card>

      {detailQ.data && (
        <div className="text-xs text-muted-foreground/60 font-mono">
          refreshed {new Date(detailQ.dataUpdatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          {power.isPending && ' · sending…'}
        </div>
      )}
    </div>
  );
}

function Tile({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <Card>
      <CardBody>
        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-0.5">{label}</div>
        <div className="text-xl font-semibold tabular-nums">{value}</div>
        {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
      </CardBody>
    </Card>
  );
}

function ActionButton({
  icon: Icon,
  label,
  tone = 'default',
  onClick,
  disabled,
}: {
  icon: typeof Play;
  label: string;
  tone?: 'default' | 'success' | 'danger';
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex items-center gap-1 px-2.5 py-1 rounded-md text-xs border transition-colors',
        disabled
          ? 'border-border/30 text-muted-foreground/40 cursor-not-allowed'
          : tone === 'success'
            ? 'border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10'
            : tone === 'danger'
              ? 'border-rose-500/40 text-rose-300 hover:bg-rose-500/10'
              : 'border-border text-foreground hover:bg-accent',
      )}
    >
      <Icon className="h-3 w-3" />
      {label}
    </button>
  );
}
